/** A site's grid region, **chosen from what the factor library prices** and never typed.
 *
 * One control for the two places a region is set - the form that adds a site and the form that
 * corrects one - because electricity at a site cannot be priced without it (US walkthrough, B2: a
 * new site had no region, the add form never asked, and the first electricity entry failed three
 * screens away).
 *
 * The list is the server's (`GET /grid-regions`), so the control offers exactly what a save
 * accepts. **Names are EPA's, served as data** (`factors/egrid_regions`, verification row C-10d),
 * shown beside the code: a customer knows "WECC California", not `CAMX`. The option *value* stays the code, which is
 * what the server stores and prices by. Three honest states, none defaulted:
 *
 * - **Not set** is a first-class choice. It is said plainly: electricity for the site cannot be
 *   priced until a region is chosen, and nothing here guesses one (a Californian site is not
 *   necessarily `CAMX`).
 * - **A region the library does not price** (a value from before the list was enforced) is shown
 *   as what it is rather than as "not set", so the screen never hides a stored value.
 * - **A failed read** says so and offers a retry. Free text is *not* the fallback: the server
 *   refuses a region it does not price, so a typed value would be a control that predictably fails.
 *
 * **`mode="add"` asks.** Where the library offers a real choice (more than one region) the site
 * form requires an answer, and the one way to leave it is an explicit "I do not know yet" that says
 * what it costs. An optional field that is skipped by default is the defect this was written for.
 */

import { useGridRegions } from "../api/gridRegions";
import { GRID_UNKNOWN, regionLabel } from "./gridRegionChoice";
import own from "./Organisation.module.css";
import type { JurisdictionValue } from "./organisationOptions";

function cls(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(" ");
}

export function GridRegionField({
  jurisdiction,
  value,
  stored,
  onChange,
  mode = "correct",
  idBase = "correct-site",
}: {
  jurisdiction: JurisdictionValue;
  value: string;
  /** What the site has now (correction only). */
  stored: string | null;
  onChange: (next: string) => void;
  mode?: "add" | "correct";
  idBase?: string;
}) {
  const regions = useGridRegions(jurisdiction);
  const listed = regions.data?.regions ?? [];
  const adding = mode === "add";
  const unknown = value === GRID_UNKNOWN;
  const chosen = listed.find((region) => region.code === value);
  const failed = regions.error !== null;
  const unpriced =
    value !== "" && !unknown && regions.data !== undefined && chosen === undefined;
  // A choice exists only where there is more than one region. With one (the UK's single grid) the
  // server fills it in and asking would be asking for a constant.
  const mustChoose = adding && listed.length > 1;
  // The add form sits on the same page as the correction form, so its hints carry their own ids.
  const t = (name: string) => (adding ? `add-site-${name}` : name);
  return (
    <div className={cls(own.field)}>
      <label className={cls(own.label)} htmlFor={`${idBase}-grid`}>
        {adding ? "Which electricity grid is it on?" : "Grid region"}
      </label>
      <select
        id={`${idBase}-grid`}
        className={cls(own.input)}
        value={value}
        disabled={regions.isPending || failed}
        required={mustChoose}
        data-testid={`${idBase}-grid`}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{adding ? "Choose a grid region" : "Not set yet"}</option>
        {adding && listed.length > 1 ? (
          <option value={GRID_UNKNOWN}>I do not know yet</option>
        ) : null}
        {(unpriced || (failed && value !== "")) && (
          <option value={value}>{unpriced ? `${value} (not a region EmitIQ prices)` : value}</option>
        )}
        {listed.map((region) => (
          <option key={region.code} value={region.code}>
            {regionLabel(region)}
          </option>
        ))}
      </select>
      {failed ? (
        <span className={cls(own.hint)} role="alert" data-testid={t("grid-region-list-failed")}>
          The list of grid regions could not be read, so the region cannot be changed from here
          right now.{" "}
          <button
            type="button"
            className={cls(own.cancel)}
            data-testid={t("grid-region-list-retry")}
            onClick={() => void regions.refetch()}
          >
            Read the grid regions again
          </button>
        </span>
      ) : null}
      {value === "" && !adding ? (
        <span className={cls(own.hint)} data-testid={t("grid-region-not-set")}>
          Not set. Figures for this site cannot be priced yet: a bill for its electricity is
          refused until a grid region is chosen.
          {stored === null ? "" : " Saving this clears the region the site has now."}
        </span>
      ) : null}
      {unknown ? (
        <span className={cls(own.hint)} data-testid={t("grid-region-unknown")}>
          The site will be added with no grid region. Electricity for it cannot be recorded until
          you set one, under the site&rsquo;s Correct details.
        </span>
      ) : null}
      {unpriced ? (
        <span className={cls(own.hint)} role="alert" data-testid={t("grid-region-not-priced")}>
          {value} is not a region EmitIQ has electricity factors for in this jurisdiction, so
          electricity at this site cannot be priced. Choose one from the list.
        </span>
      ) : null}
      {chosen !== undefined && chosen.states.length > 0 ? (
        <span className={cls(own.hint)} data-testid={t("grid-region-states")}>
          EPA lists power plants in this region in {chosen.states.join(", ")}. A state can sit in
          more than one region, so check which grid your utility serves.
        </span>
      ) : null}
      {chosen !== undefined ? (
        <span className={cls(own.hint)} data-testid={t("grid-region-source")}>
          {chosen.publisher ?? "The publisher"}, {chosen.edition ?? "edition not loaded"}
          {chosen.external_id ? ` (${chosen.external_id})` : ""}
          {chosen.verification_row ? `, verification row ${chosen.verification_row}` : ""}.
        </span>
      ) : null}
      {regions.data !== undefined ? (
        <span className={cls(own.hint)} data-testid={t("grid-region-list-note")}>
          {listed.length === 1
            ? `The only region EmitIQ publishes for this jurisdiction is ${listed[0]?.code ?? ""}.`
            : `${listed.length} regions have electricity factors in EmitIQ. It cannot tell which one a site is on; your electricity bill or utility will say.`}
        </span>
      ) : null}
      {adding ? null : (
        <span className={cls(own.hint)} data-testid={t("grid-region-is-for-new-records")}>
          Applies to readings recorded from now on. A reading already recorded keeps the region it
          was recorded with, so no figure that exists moves.
        </span>
      )}
    </div>
  );
}
